import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    TraversalOrder,
    closestCorners,
    pointerWithin,
    useSensor,
    useSensors,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { STAGE_META, STAGE_ORDER } from '@vanilla/constants/Stages.js';
import { formatDateDisplay } from '@vanilla/utils/Utils.js';
import { sortKanbanDeals } from '@vanilla/utils/KanbanDealSort.js';
import { getKanbanSortPref } from '@vanilla/constants/KanbanSortPrefs.js';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { TooltipProvider } from '@/components/ui/tooltip';
import KanbanColumn from './KanbanColumn.jsx';
import DealCard from './DealCard.jsx';

function isInsightItemDone(item) {
    const status = String(item?.status || '').toLowerCase();
    return item?.is_completed === true
        || item?.is_completed === 'true'
        || item?.completed === true
        || item?.completed === 'true'
        || status === 'done'
        || status === 'completed';
}

async function loadInsightItems(dealId, kind, cache) {
    const cacheKey = `${dealId}:${kind}:pending`;
    const cached = cache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.ts < 120000) return cached.items;

    let items = [];
    if (kind === 'nextsteps') {
        const response = await DealsApiService.getNextSteps(dealId);
        const rows = Array.isArray(response) ? response : (response?.data || []);
        items = rows
            .filter((step) => !isInsightItemDone(step))
            .slice()
            .sort((a, b) => {
                const aDue = a.due_date || a.dueDate || '';
                const bDue = b.due_date || b.dueDate || '';
                if (aDue && bDue) return String(aDue).localeCompare(String(bDue));
                if (aDue) return -1;
                if (bDue) return 1;
                return (a.sort_order ?? 0) - (b.sort_order ?? 0);
            })
            .map((step) => ({
                title: step.action || 'Next step',
                meta: step.due_date || step.dueDate
                    ? `Due ${formatDateDisplay(step.due_date || step.dueDate)}`
                    : 'No due date',
            }));
    } else if (kind === 'tasks' || kind === 'comments') {
        const actKey = `${dealId}:user-activities`;
        let value = cache.get(actKey)?.value;
        if (!value || now - (cache.get(actKey)?.ts || 0) >= 120000) {
            const rows = await ActivityService.getForEntity('deal', dealId, {
                perPage: 100,
                sortOrder: 'desc',
                source: 'user',
            });
            const taskItems = rows
                .filter((a) => String(a.type || a.activityType || '').toLowerCase() === 'task')
                .filter((task) => !isInsightItemDone(task))
                .map((task) => ({
                    title: task.subject || 'Task',
                    meta: task.dueDate ? `Due ${formatDateDisplay(task.dueDate)}` : 'No due date',
                }));
            const commentCount = rows.filter((a) => {
                const type = String(a.type || a.activityType || '').toLowerCase();
                return type === 'comment' || type === 'note';
            }).length;
            value = { taskItems, commentCount };
            cache.set(actKey, { ts: now, value });
        }
        if (kind === 'tasks') items = value.taskItems;
        else {
            items = Array.from({ length: value.commentCount }, (_, i) => ({
                title: 'Comment',
                meta: i === 0 ? `${value.commentCount} comment${value.commentCount === 1 ? '' : 's'}` : '',
            })).slice(0, Math.min(value.commentCount, 1));
            if (value.commentCount > 0) {
                items = [{ title: `${value.commentCount} comment${value.commentCount === 1 ? '' : 's'}`, meta: 'Open deal to view timeline' }];
            }
        }
    }

    cache.set(cacheKey, { ts: now, items });
    return items;
}

const dropAnimationConfig = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {
                opacity: '0.4',
            },
        },
    }),
};

export default function KanbanBoard({ deals, onMove, onEdit, onSort, version }) {
    const boardRef = useRef(null);
    const boardWrapperRef = useRef(null);
    const savedScrollLeftRef = useRef(0);
    const isRestoringScrollRef = useRef(false);
    const dropLockRef = useRef(false);
    const pendingDropStageRef = useRef(null);
    const unlockTimerRef = useRef(null);
    const cacheRef = useRef(new Map());
    const hydrateToken = useRef(0);
    const [insightByDeal, setInsightByDeal] = useState({});
    const [popover, setPopover] = useState(null);
    const [activeDeal, setActiveDeal] = useState(null);
    const [overStage, setOverStage] = useState(null);
    const [scrollLocked, setScrollLocked] = useState(false);

    const captureScroll = useCallback(() => {
        const el = boardWrapperRef.current;
        if (!el || dropLockRef.current) return;
        savedScrollLeftRef.current = el.scrollLeft;
    }, []);

    const scrollStageIntoView = useCallback((stageKey) => {
        const wrapper = boardWrapperRef.current;
        if (!wrapper) return;
        if (stageKey) {
            const col = wrapper.querySelector(`.kanban-column[data-stage="${stageKey}"]`);
            if (col) {
                const next = Math.max(0, Math.round(
                    wrapper.scrollLeft + (col.getBoundingClientRect().left - wrapper.getBoundingClientRect().left)
                ));
                savedScrollLeftRef.current = next;
                wrapper.scrollLeft = next;
                return;
            }
        }
        if (typeof savedScrollLeftRef.current === 'number') {
            wrapper.scrollLeft = savedScrollLeftRef.current;
        }
    }, []);

    const restoreScroll = useCallback(() => {
        const el = boardWrapperRef.current;
        if (!el) return;
        isRestoringScrollRef.current = true;
        scrollStageIntoView(pendingDropStageRef.current);
        const target = savedScrollLeftRef.current;
        requestAnimationFrame(() => {
            if (!el) return;
            if (pendingDropStageRef.current) {
                scrollStageIntoView(pendingDropStageRef.current);
            } else if (typeof target === 'number') {
                el.scrollLeft = target;
            }
            requestAnimationFrame(() => {
                if (el && typeof savedScrollLeftRef.current === 'number') {
                    el.scrollLeft = savedScrollLeftRef.current;
                }
                isRestoringScrollRef.current = false;
            });
        });
    }, [scrollStageIntoView]);

    const releaseScrollLock = useCallback(() => {
        if (unlockTimerRef.current) {
            clearTimeout(unlockTimerRef.current);
            unlockTimerRef.current = null;
        }
        dropLockRef.current = false;
        pendingDropStageRef.current = null;
        setScrollLocked(false);
        const el = boardWrapperRef.current;
        if (el) savedScrollLeftRef.current = el.scrollLeft;
    }, []);

    const holdScrollLock = useCallback((ms = 400) => {
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = setTimeout(() => {
            restoreScroll();
            unlockTimerRef.current = setTimeout(releaseScrollLock, 50);
        }, ms);
    }, [releaseScrollLock, restoreScroll]);

    useEffect(() => {
        const el = boardWrapperRef.current;
        if (!el) return undefined;
        const handleScroll = () => {
            if (dropLockRef.current || isRestoringScrollRef.current) return;
            savedScrollLeftRef.current = el.scrollLeft;
        };
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => () => {
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    }, []);

    useLayoutEffect(() => {
        if (!dropLockRef.current) return;
        restoreScroll();
    }, [deals, version, activeDeal, restoreScroll]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const collisionDetection = useCallback((args) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) {
            return pointerCollisions;
        }
        return closestCorners(args);
    }, []);

    const columns = useMemo(() => {
        const byStage = new Map();
        for (const stageKey of STAGE_ORDER) {
            byStage.set(stageKey, []);
        }
        for (const d of deals) {
            const list = byStage.get(d.dealStage);
            if (list) list.push(d);
        }
        return STAGE_ORDER.map((stageKey) => {
            const stageDeals = byStage.get(stageKey) || [];
            const pref = getKanbanSortPref(stageKey);
            return { stageKey, deals: sortKanbanDeals(stageDeals, pref.key, pref.dir) };
        });
    }, [deals, version]);

    useEffect(() => {
        const token = ++hydrateToken.current;
        const ids = deals.map((d) => d.id).filter(Boolean);
        if (!ids.length) {
            setInsightByDeal({});
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const raw = await DealsApiService.getInsightCounts(ids);
                if (cancelled || hydrateToken.current !== token) return;
                const next = {};
                for (const deal of deals) {
                    const row = raw?.[deal.id] || raw?.[String(deal.id)] || {};
                    next[deal.id] = {
                        nextSteps: Number(row.next_steps ?? row.nextSteps ?? 0) || 0,
                        tasks: Number(row.tasks ?? 0) || 0,
                        comments: Number(row.comments ?? 0) || 0,
                    };
                }
                setInsightByDeal(next);
            } catch (err) {
                console.warn('[KanbanBoard] batch insight counts failed:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [deals, version]);

    useEffect(() => {
        const onChanged = (e) => {
            const detail = e.detail || {};
            const dealId = detail.dealId;
            if (!dealId) return;
            cacheRef.current.clear();
            if (
                detail.pendingNextStepCount != null
                || detail.pendingTaskCount != null
            ) {
                setInsightByDeal((prev) => ({
                    ...prev,
                    [dealId]: {
                        nextSteps: detail.pendingNextStepCount != null
                            ? Number(detail.pendingNextStepCount)
                            : (prev[dealId]?.nextSteps ?? 0),
                        tasks: detail.pendingTaskCount != null
                            ? Number(detail.pendingTaskCount)
                            : (prev[dealId]?.tasks ?? 0),
                        comments: prev[dealId]?.comments ?? 0,
                    },
                }));
            }
            const ids = deals.map((d) => d.id).filter(Boolean);
            if (!ids.length) return;
            DealsApiService.getInsightCounts(ids).then((raw) => {
                const next = {};
                for (const deal of deals) {
                    const row = raw?.[deal.id] || raw?.[String(deal.id)] || {};
                    next[deal.id] = {
                        nextSteps: Number(row.next_steps ?? row.nextSteps ?? 0) || 0,
                        tasks: Number(row.tasks ?? 0) || 0,
                        comments: Number(row.comments ?? 0) || 0,
                    };
                }
                setInsightByDeal(next);
            }).catch(() => {});
        };
        window.addEventListener('dealActivitiesChanged', onChanged);
        return () => window.removeEventListener('dealActivitiesChanged', onChanged);
    }, [deals]);

    useEffect(() => {
        if (!popover) return undefined;
        const close = (e) => {
            if (e.target.closest('.kanban-card-insights-popover') || e.target.closest('.kanban-insight-btn')) return;
            setPopover(null);
        };
        const onKey = (e) => { if (e.key === 'Escape') setPopover(null); };
        window.addEventListener('click', close);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('keydown', onKey);
        };
    }, [popover]);

    const onInsight = useCallback(async (anchorBtn, deal, kind) => {
        if (
            popover
            && String(popover.dealId) === String(deal.id)
            && popover.kind === kind
        ) {
            setPopover(null);
            return;
        }
        const rect = anchorBtn.getBoundingClientRect();
        const left = Math.max(8, Math.min(window.innerWidth - 300, rect.left));
        const top = rect.bottom + 6;
        setPopover({
            dealId: deal.id,
            dealName: deal.dealName || deal.name || 'Deal',
            kind,
            left,
            top,
            loading: true,
            items: [],
            index: 0,
        });
        try {
            const items = await loadInsightItems(deal.id, kind, cacheRef.current);
            setPopover((prev) => {
                if (!prev || String(prev.dealId) !== String(deal.id) || prev.kind !== kind) return prev;
                return { ...prev, loading: false, items, index: 0 };
            });
            if (kind !== 'comments') {
                setInsightByDeal((prev) => ({
                    ...prev,
                    [deal.id]: {
                        ...(prev[deal.id] || {}),
                        [kind === 'nextsteps' ? 'nextSteps' : 'tasks']: items.length,
                    },
                }));
            }
        } catch (err) {
            console.error('Failed to load card insights:', err);
            setPopover((prev) => {
                if (!prev || String(prev.dealId) !== String(deal.id) || prev.kind !== kind) return prev;
                return { ...prev, loading: false, error: true, items: [] };
            });
        }
    }, [popover]);

    const resolveOverStage = useCallback((over) => {
        if (!over) return null;
        if (over.data?.current?.type === 'column') {
            return over.data.current.stage || over.id;
        }
        if (over.data?.current?.type === 'deal') {
            return over.data.current.stage || over.data.current.deal?.dealStage || null;
        }
        if (STAGE_ORDER.includes(over.id)) return over.id;
        return null;
    }, []);

    const handleDragStart = useCallback((event) => {
        const { active } = event;
        dropLockRef.current = false;
        pendingDropStageRef.current = null;
        captureScroll();
        setScrollLocked(true);
        const deal = active.data?.current?.deal || deals.find((d) => String(d.id) === String(active.id));
        if (deal) {
            setActiveDeal(deal);
            const initialStage = active.data?.current?.stage || deal.dealStage;
            setOverStage(initialStage || null);
        }
    }, [captureScroll, deals]);

    const handleDragMove = useCallback(() => {
        captureScroll();
    }, [captureScroll]);

    const handleDragOver = useCallback((event) => {
        captureScroll();
        const targetStage = resolveOverStage(event.over);
        setOverStage(targetStage);
    }, [captureScroll, resolveOverStage]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        const targetStage = resolveOverStage(over);
        dropLockRef.current = true;
        pendingDropStageRef.current = targetStage || overStage;
        setActiveDeal(null);
        setOverStage(null);
        restoreScroll();
        holdScrollLock(350);

        if (!over) return;

        const dealId = active.id;
        const sourceStage = active.data?.current?.stage || active.data?.current?.deal?.dealStage;

        if (dealId && targetStage && targetStage !== sourceStage) {
            onMove(dealId, targetStage);
        }
    }, [holdScrollLock, onMove, overStage, resolveOverStage, restoreScroll]);

    const handleDragCancel = useCallback(() => {
        dropLockRef.current = true;
        pendingDropStageRef.current = overStage;
        setActiveDeal(null);
        setOverStage(null);
        restoreScroll();
        holdScrollLock(350);
    }, [holdScrollLock, overStage, restoreScroll]);

    const emptyLabel = popover?.kind === 'nextsteps'
        ? 'No pending next steps'
        : popover?.kind === 'tasks'
            ? 'No pending tasks'
            : 'No comments yet';

    const autoScrollConfig = useMemo(() => ({
        threshold: { x: 0.2, y: 0.2 },
        acceleration: 10,
        order: TraversalOrder.ReversedTreeOrder,
    }), []);

    return (
        <TooltipProvider delayDuration={200}>
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
                autoScroll={autoScrollConfig}
            >
                <section id="kanbanView" className="view-section active">
                    <div className="kanban-card-mock">
                        <div
                            id="kanbanBoardWrapper"
                            className={`kanban-board-wrapper ${activeDeal || scrollLocked ? 'is-dnd-dragging is-touch-dragging' : ''}`}
                            ref={boardWrapperRef}
                        >
                            <div className="kanban-board" id="kanbanBoard" ref={boardRef}>
                                {columns.map(({ stageKey, deals: stageDeals }) => (
                                    <KanbanColumn
                                        key={stageKey}
                                        stageKey={stageKey}
                                        deals={stageDeals}
                                        onEdit={onEdit}
                                        onSort={onSort}
                                        onInsight={onInsight}
                                        insightByDeal={insightByDeal}
                                        isDropTarget={overStage === stageKey}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <DragOverlay dropAnimation={dropAnimationConfig} wrapperElement="div">
                        {activeDeal ? (
                            <DealCard
                                deal={activeDeal}
                                meta={STAGE_META[activeDeal.dealStage] || {}}
                                isOverlay
                                insightCounts={insightByDeal[activeDeal.id] || insightByDeal[String(activeDeal.id)]}
                            />
                        ) : null}
                    </DragOverlay>

                    {popover && (
                        <div
                            className="kanban-card-insights-popover"
                            style={{ position: 'fixed', left: popover.left, top: popover.top, zIndex: 1300 }}
                            role="dialog"
                            aria-label="Deal insights"
                        >
                            {popover.loading && <div className="kanban-card-insights-loading">Loading...</div>}
                            {popover.error && <div className="kanban-card-insights-error">Could not load. Try again.</div>}
                            {!popover.loading && !popover.error && (
                                <>
                                    <div className="kanban-card-insights-head">
                                        {popover.kind === 'nextsteps' ? 'Next steps' : popover.kind === 'tasks' ? 'Tasks' : 'Comments'}
                                    </div>
                                    {popover.items.length ? (
                                        <>
                                            <div className="kanban-card-insights-body">
                                                <div className="kanban-card-insights-item-title">
                                                    {popover.items[popover.index]?.title || ''}
                                                </div>
                                                <div className="kanban-card-insights-item-meta">
                                                    <span className="kanban-card-insights-status is-pending">Pending</span>
                                                    {' '}{popover.items[popover.index]?.meta || ''}
                                                </div>
                                            </div>
                                            {popover.items.length > 1 && (
                                                <div className="kanban-card-insights-nav">
                                                    <button
                                                        type="button"
                                                        className="kanban-card-insights-nav-btn"
                                                        disabled={popover.index === 0}
                                                        aria-label="Previous"
                                                        onClick={() => setPopover((p) => ({ ...p, index: Math.max(0, p.index - 1) }))}
                                                    >
                                                        &lt;
                                                    </button>
                                                    <span>{popover.index + 1} / {popover.items.length}</span>
                                                    <button
                                                        type="button"
                                                        className="kanban-card-insights-nav-btn"
                                                        disabled={popover.index >= popover.items.length - 1}
                                                        aria-label="Next"
                                                        onClick={() => setPopover((p) => ({ ...p, index: Math.min(p.items.length - 1, p.index + 1) }))}
                                                    >
                                                        &gt;
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="kanban-card-insights-empty">{emptyLabel}</div>
                                    )}
                                    <button
                                        type="button"
                                        className="kanban-card-insights-open"
                                        onClick={() => {
                                            const kind = popover.kind;
                                            const dealId = popover.dealId;
                                            setPopover(null);
                                            if (kind === 'comments') {
                                                onEdit(dealId, { sidebarTab: 'timeline', timelineFilter: 'comments', openDrawer: false });
                                            } else {
                                                const sidebarTab = kind === 'tasks' ? 'tasks' : 'nextsteps';
                                                onEdit(dealId, { sidebarTab });
                                            }
                                        }}
                                    >
                                        View all in deal -&gt;
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </section>
            </DndContext>
        </TooltipProvider>
    );
}
